import { useEntityFilter } from '@risksmart-app/components/src/contexts/entityFilterContext';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import type { ParentIssueType } from '@risksmart-app/domain/src/types/consts';
import type { IssueDashboardDateField } from '@risksmart-app/domain/src/types/consts/dashboard-date-fields';
import type { IssueRegisterResponseRow } from '@risksmart-app/drizzle/src/dto';
import type { IssueRegisterResponse } from '@risksmart-app/trpc/src/types';
import type {
  GetIssuesQuery,
  Issue_Bool_Exp,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetIssuesDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import useEntityWhereFilter from 'src/hooks/useEntityWhereFilter';
import { createQueryHook, type StrictMappedGraphQLSelection } from 'src/utils';

import { ISSUE_DATE_PREDICATES } from '@/hooks/queries/dashboardDatePredicates';
import {
  buildDashboardWhere,
  type DashboardFilterArgs,
  departmentsWhereOnEntity,
  normaliseArrayFilter,
  tagsWhereOnEntity,
} from '@/hooks/queries/dashboardFilterArgs';
import type { GraphQLFetchPolicy } from '@/utils/hook-factories/types';

function mapTrpcIssueToGraphQL(
  issue: IssueRegisterResponseRow
): StrictMappedGraphQLSelection<GetIssuesQuery['issue'][number]> {
  return {
    ...issue,
    // `getIssuesRegisterQueryConfig` pre-filters `actions` to Status = open, so
    // `.length` is the open-action count the GraphQL aggregate exposed. Do not
    // re-filter here — and do not drop the predicate server-side, or this
    // silently becomes a total (COI-747).
    actions_aggregate: {
      aggregate: {
        count: issue.actions.length,
      },
    },
  };
}

/**
 * Maps TRPC issue data to match the GraphQL query structure
 * Exported for use in data sources
 */
export function mapTrpcIssuesToGraphQL(
  trpcData: IssueRegisterResponse | undefined
): StrictMappedGraphQLSelection<GetIssuesQuery> | undefined {
  if (!trpcData) {
    return undefined;
  }

  return {
    issue: trpcData.issue.map((issue) => mapTrpcIssueToGraphQL(issue)),
  };
}

type InternalArgs = UseGetIssueRegisterArgs & {
  entityIds?: string[];
};

const useIssueRegisterGraphqlVariables = (args: InternalArgs) => {
  const { issueType } = args;
  const { user } = useRisksmartUser();
  // COI-602 — `undefined` (not `{ Type: { _eq: undefined } }`) so the hook's
  // own `defaultValue = {} as T` applies and the variables match the raw call
  // the `issueAction` descriptor used to make: entity filter, no `Type`.
  const entityWhere = useEntityWhereFilter<Issue_Bool_Exp>(
    Parent_Type_Enum.Issue,
    issueType ? { Type: { _eq: issueType } } : undefined
  );

  const { where } = buildDashboardWhere({
    base: entityWhere,
    filters: args,
    userId: user?.userId,
    datePredicates: ISSUE_DATE_PREDICATES,
    tagsWhere: tagsWhereOnEntity,
    departmentsWhere: departmentsWhereOnEntity,
    ownershipWhere: (conditions) => conditions.issueFilterConditions,
  });

  return { where };
};

const useGetIssueRegisterBase = createQueryHook<
  InternalArgs,
  IssueRegisterResponse,
  GetIssuesQuery
>({
  trpcQueryOptions: (
    trpc,
    {
      issueType,
      entityIds,
      tagTypeIds,
      departmentTypeIds,
      dateRange,
      ownership,
    }
  ) =>
    trpc.frontend.issue.register.queryOptions({
      issueType,
      entityIds,
      tagTypeIds: normaliseArrayFilter(tagTypeIds),
      departmentTypeIds: normaliseArrayFilter(departmentTypeIds),
      dateRange,
      ownership,
    }),
  mapTrpcDataToGraphQL: (data) => ({
    issue: data.issue.map((issue) => mapTrpcIssueToGraphQL(issue)),
  }),
  graphqlDocument: GetIssuesDocument,
  graphqlVariables: useIssueRegisterGraphqlVariables,
});

export type UseGetIssueRegisterArgs = {
  /**
   * Omit to span every issue subtype (COI-602). The `issueAction`
   * relationship widget is one descriptor covering all of them, so it has no
   * subtype to pass; the per-subtype register page and single-entity
   * descriptor both still supply one.
   */
  issueType?: ParentIssueType;
} & DashboardFilterArgs<IssueDashboardDateField>;

/**
 * Fetches issues with automatic entity filtering for both GraphQL and tRPC
 * paths. Entity filtering uses useEntityFilter context internally — consumers
 * don't need to pass entityIds. The dashboard's global filters (COI-691) are
 * all optional: passing none reproduces the register page's variables exactly.
 */
export const useGetIssueRegister = (params: {
  queryArgs: UseGetIssueRegisterArgs;
  shouldSkip?: boolean;
  fetchPolicy?: GraphQLFetchPolicy;
  suppressErrorNotification?: boolean;
}) => {
  const { entityIds } = useEntityFilter();

  return useGetIssueRegisterBase({
    queryArgs: {
      ...params.queryArgs,
      entityIds: entityIds?.length ? entityIds : undefined,
    },
    shouldSkip: params.shouldSkip,
    fetchPolicy: params.fetchPolicy,
    suppressErrorNotification: params.suppressErrorNotification,
  });
};
